/**
 * File: client/src/components/PropertyDetails.js
 * ===================================================
 * Route: /property/:slug
 *
 * Role:
 * - Displays the full detail view for a single property
 * - Resolves property by either:
 *   a) address-based slug (preferred)
 *   b) fallback system ID slug: property-<id>
 *
 * Interacts with:
 * - PropertyDashboard (slug generation)
 * - OverviewTab (financial snapshot)
 * - HomeRunTab / Tasks (Next Actions)
 * - PropertyTabs (tab navigation)
 *
 * This file is the canonical resolver for property identity.
 */

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PropertyTabs from "./PropertyTabs.js";
import { getStreetViewUrl } from "../utils/geocoding.js";
import NotificationBell from "./Notifications/NotificationBell.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import {
  FilePlus,
  Wrench,
  LineChart,
  ArrowLeft,
  Trash2,
  Pencil,
  X,
  Loader2,
} from "lucide-react";

const PROPERTY_TYPES = [
  "Detached",
  "Semi-Detached",
  "Townhouse",
  "Condo / Apartment",
  "Duplex",
  "Other",
];

/**
 * slugify
 * ---------------------------------------------------
 * MUST match logic used in PropertyDashboard.js
 */
const slugify = (str = "") =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");

export default function PropertyDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  /**
   * Normalize image URLs - use Street View as fallback
   */
  const resolveImageUrl = (prop) => {
    if (prop.imageUrl) {
      if (prop.imageUrl.startsWith("http")) return prop.imageUrl;
      return prop.imageUrl;
    }
    // Fallback to Street View if we have address or coordinates
    if (prop.address) {
      return getStreetViewUrl(prop.address, { size: "800x400" });
    }
    if (prop.lat && prop.lng) {
      return getStreetViewUrl(`${prop.lat},${prop.lng}`, { size: "800x400" });
    }
    return null;
  };

  /**
   * Fetch and resolve property by slug
   */
  useEffect(() => {
    async function loadProperty() {
      try {
        setLoading(true);
        const token = getToken();

        const res = await fetch("/api/properties", {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!res.ok) throw new Error("Failed to load properties");

        const allProperties = await res.json();

        const matched = allProperties.find((p) => {
          // Fallback ID-based routing
          if (slug.startsWith("property-")) {
            return `property-${p.id}` === slug;
          }

          // Address-based routing
          return p.address && slugify(p.address) === slug;
        });

        if (!matched) {
          throw new Error("Property not found");
        }

        setProperty(matched);
      } catch (err) {
        console.error(err);
        setError("Could not load property details.");
      } finally {
        setLoading(false);
      }
    }

    loadProperty();
  }, [slug, getToken]);

  /* ===================================================
     LOADING STATE
  =================================================== */
  if (loading) {
    return (
      <div className="px-6 py-8 max-w-5xl mx-auto animate-pulse space-y-6">
        <div className="h-64 bg-gray-200 rounded-xl" />
        <div className="h-10 bg-gray-200 rounded-md w-1/2" />
        <div className="h-6 bg-gray-200 rounded-md w-1/3" />
      </div>
    );
  }

  /* ===================================================
     ERROR STATE
  =================================================== */
  if (error || !property) {
    return (
      <div className="p-6 text-red-600">
        {error || "Property not found."}
      </div>
    );
  }

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const token = getToken();
      const res = await fetch(`/api/properties/${property.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to delete property");
      navigate("/");
    } catch (err) {
      setError(err.message);
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const openEdit = () => {
    setEditForm({
      type: property.type || "",
      currentValue: property.currentValue ?? property.valuation?.estimate ?? "",
      mortgageBalance: property.mortgage?.balance ?? "",
      rate: property.mortgage?.rate ?? "",
      renewalDate: property.mortgage?.renewalDate ?? "",
      monthlyPayment: property.mortgage?.monthlyPayment ?? "",
      propertyTax: property.propertyTax ?? "",
    });
    setEditError(null);
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setEditError(null);
    try {
      const token = getToken();
      const n = (v) => (v !== "" && v !== null && v !== undefined ? Number(v) : null);

      const payload = {
        type: editForm.type || undefined,
        currentValue: n(editForm.currentValue),
        propertyTax: n(editForm.propertyTax),
        valuation: {
          ...property.valuation,
          estimate: n(editForm.currentValue),
        },
        mortgage: {
          ...property.mortgage,
          balance: n(editForm.mortgageBalance),
          rate: n(editForm.rate),
          renewalDate: editForm.renewalDate || null,
          monthlyPayment: n(editForm.monthlyPayment),
        },
      };

      const res = await fetch(`/api/properties/${property.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to save changes");
      }

      const updated = await res.json();
      setProperty(updated);
      setEditing(false);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const imageSrc = resolveImageUrl(property);

  const valuation = property.valuation?.estimate ?? null;
  const mortgageBalance = property.mortgage?.balance ?? null;
  const equity =
    valuation !== null && mortgageBalance !== null
      ? valuation - mortgageBalance
      : null;

  /* ===================================================
     MAIN RENDER
  =================================================== */
  return (
    <div className="px-6 py-8 max-w-5xl mx-auto space-y-6 bg-hn-white text-hn-dark">

      {/* =================================================
          TOP NAV BAR
      ================================================= */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-gray-600 hover:text-hn-dark transition"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">Back to Portfolio</span>
        </button>

        <NotificationBell userId="owner" />
      </div>

      {/* =================================================
          HEADER
      ================================================= */}
      <div className="rounded-xl shadow-sm border border-hn-dark/5 bg-white p-6 flex flex-col md:flex-row gap-8">

        {/* IMAGE */}
        <div className="w-full md:w-1/3">
          {imageSrc ? (
            <img
              src={imageSrc}
              alt="Property"
              className="w-full rounded-xl object-cover aspect-video border border-gray-200"
              onError={(e) => (e.target.style.display = "none")}
            />
          ) : (
            <div className="w-full aspect-video bg-gray-200 rounded-xl" />
          )}
        </div>

        {/* INFO */}
        <div className="flex-1 space-y-3">
          <h1 className="text-3xl font-lemonmilk tracking-tight">
            {property.address || property.name || "Property"}
          </h1>

          {property.type && (
            <p className="text-gray-600 text-sm">{property.type}</p>
          )}

          {/* SNAPSHOT GRID */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 text-sm">

            {/* VALUATION */}
            <div className="p-4 bg-gray-50 rounded-xl border">
              <p className="font-semibold text-hn-red">
                Current Valuation
              </p>
              <p className="text-lg font-bold mt-1">
                {valuation
                  ? `$${valuation.toLocaleString()}`
                  : "—"}
              </p>
            </div>

            {/* MORTGAGE */}
            <div className="p-4 bg-gray-50 rounded-xl border">
              <p className="font-semibold text-hn-red">
                Mortgage Balance
              </p>
              <p className="text-lg font-bold mt-1">
                {mortgageBalance
                  ? `$${mortgageBalance.toLocaleString()}`
                  : "—"}
              </p>
            </div>

            {/* EQUITY */}
            <div className="p-4 bg-gray-50 rounded-xl border">
              <p className="font-semibold text-hn-red">
                Home Equity
              </p>
              <p className="text-lg font-bold mt-1">
                {equity !== null
                  ? `$${equity.toLocaleString()}`
                  : "—"}
              </p>
            </div>

          </div>

          {/* CTA BAR */}
          <div className="flex gap-4 mt-6 flex-wrap">
            <button
              onClick={() => setActiveTab("documents")}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-hn-dark rounded-lg shadow-sm hover:bg-gray-200 transition text-sm"
            >
              <FilePlus className="w-4 h-4" />
              Upload Document
            </button>

            <button
              onClick={() => setActiveTab("homerun")}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-hn-dark rounded-lg shadow-sm hover:bg-gray-200 transition text-sm"
            >
              <Wrench className="w-4 h-4" />
              Add Maintenance
            </button>

            <button
              onClick={() => setActiveTab("finances")}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-hn-dark rounded-lg shadow-sm hover:bg-gray-200 transition text-sm"
            >
              <LineChart className="w-4 h-4" />
              Run Analysis
            </button>

            <button
              onClick={openEdit}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-hn-dark border border-red-400 rounded-lg shadow-sm hover:bg-gray-200 transition text-sm"
            >
              <Pencil className="w-4 h-4" />
              Edit Property
            </button>

            <button
              onClick={() => setConfirmDelete(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-red-200 text-red-600 rounded-lg shadow-sm hover:bg-red-50 transition text-sm ml-auto"
            >
              <Trash2 className="w-4 h-4" />
              Delete Property
            </button>
          </div>

          {/* DELETE CONFIRMATION */}
          {confirmDelete && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete this property?</h3>
                <p className="text-sm text-gray-500 mb-6">
                  This will permanently remove <strong>{property.address || property.name}</strong> and all its data. This cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setConfirmDelete(false)}
                    disabled={deleting}
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition disabled:opacity-50"
                  >
                    {deleting ? "Deleting…" : "Yes, delete"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* =================================================
          EDIT MODAL
      ================================================= */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Edit Property</h2>
              <button onClick={() => setEditing(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {editError && (
                <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">{editError}</div>
              )}

              {/* Property Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label>
                <select
                  value={editForm.type}
                  onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 bg-white"
                >
                  <option value="">Select type…</option>
                  {PROPERTY_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {/* Current Value */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Value</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                  <input
                    type="number"
                    value={editForm.currentValue}
                    onChange={(e) => setEditForm({ ...editForm, currentValue: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2"
                    placeholder="1,250,000"
                  />
                </div>
              </div>

              <hr className="border-gray-100" />
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Mortgage</p>

              {/* Balance + Rate */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Balance Owing</label>
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                    <input
                      type="number"
                      value={editForm.mortgageBalance}
                      onChange={(e) => setEditForm({ ...editForm, mortgageBalance: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg pl-5 pr-2 py-2 text-sm focus:outline-none focus:ring-1"
                      placeholder="620,000"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Interest Rate</label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      value={editForm.rate}
                      onChange={(e) => setEditForm({ ...editForm, rate: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg pl-2 pr-6 py-2 text-sm focus:outline-none focus:ring-1"
                      placeholder="5.25"
                    />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                  </div>
                </div>
              </div>

              {/* Renewal Date + Monthly Payment */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Renewal Date</label>
                  <input
                    type="date"
                    value={editForm.renewalDate}
                    onChange={(e) => setEditForm({ ...editForm, renewalDate: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-1"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Monthly Payment</label>
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                    <input
                      type="number"
                      value={editForm.monthlyPayment}
                      onChange={(e) => setEditForm({ ...editForm, monthlyPayment: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg pl-5 pr-2 py-2 text-sm focus:outline-none focus:ring-1"
                      placeholder="3,400"
                    />
                  </div>
                </div>
              </div>

              {/* Annual Property Tax */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Annual Property Tax</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                  <input
                    type="number"
                    value={editForm.propertyTax}
                    onChange={(e) => setEditForm({ ...editForm, propertyTax: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2"
                    placeholder="6,500"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t border-gray-100">
              <button
                onClick={() => setEditing(false)}
                disabled={saving}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-hn-red text-white rounded-lg text-sm font-medium hover:bg-red-700 transition disabled:opacity-50"
              >
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =================================================
          TABS
      ================================================= */}
      <PropertyTabs
        property={property}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        metrics={{
          currentValue: valuation,
          valuation: property.valuation,
          mortgage: property.mortgage,
          equity: equity,
          rentalIncome: property.rentalIncome,
          lat: property.lat,
          lng: property.lng
        }}
      />
    </div>
  );
}
